/**
 * useAccountForm Hook
 * 
 * Manages account form logic including validation, detail handling, and form state.
 */

import { useMemo } from "react";
import { Account, AccountGroupDetailField, AccountDetailValue } from "../../../types";
import { AccountGroupDoc } from "./useAccountGroups";

interface UseAccountFormParams {
  formData: {
    group: string;
    accountName: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  };
  setFormData: React.Dispatch<React.SetStateAction<{
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  }>>;
  editingAccount: Account | null;
  initialFormData: {
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  } | null;
  showAccountDetails: boolean;
  initialShowAccountDetails: boolean;
  accountNameError: string;
  accountGroups: AccountGroupDoc[];
}

interface UseAccountFormReturn {
  getSelectedGroupDetails: () => AccountGroupDetailField[];
  handleDetailValueChange: (detailName: string, value: string | number) => void;
  hasFormChanged: () => boolean;
  isAccountFormValid: boolean;
}

/**
 * Hook to manage account form logic
 */
export function useAccountForm({
  formData,
  setFormData,
  editingAccount,
  initialFormData,
  showAccountDetails,
  initialShowAccountDetails,
  accountNameError,
  accountGroups,
}: UseAccountFormParams): UseAccountFormReturn {
  // Get details for selected group
  const getSelectedGroupDetails = (): AccountGroupDetailField[] => {
    if (!formData.group) return [];
    const group = accountGroups.find((g) => g.name === formData.group);
    return group?.details || [];
  };

  // Handle detail value change
  const handleDetailValueChange = (detailName: string, value: string | number): void => {
    setFormData((prev) => ({
      ...prev,
      details: {
        ...(prev.details || {}),
        [detailName]: value,
      },
    }));
  };

  // Check if form has changed from initial values
  const hasFormChanged = (): boolean => {
    if (!editingAccount || !initialFormData) return true; // Always allow for new accounts
    
    // Compare all fields
    const currentAmount = formData.amount === "" ? "0" : formData.amount;
    const initialAmount = initialFormData.amount === "" ? "0" : initialFormData.amount;
    
    return (
      formData.accountName.trim() !== initialFormData.accountName.trim() ||
      formData.group !== initialFormData.group ||
      currentAmount !== initialAmount ||
      formData.description !== initialFormData.description ||
      JSON.stringify(formData.details || {}) !== JSON.stringify(initialFormData.details || {}) ||
      showAccountDetails !== initialShowAccountDetails
    );
  };

  // Validate account form - check if all required fields are filled
  const isAccountFormValid = useMemo<boolean>(() => {
    // Required fields: group, accountName, currency
    const hasGroup = Boolean(formData.group && formData.group.trim() !== "");
    const hasAccountName = Boolean(formData.accountName && formData.accountName.trim() !== "");
    const hasCurrency = Boolean(formData.currency && formData.currency.trim() !== "");
    
    // Also check for duplicate name error
    const hasNoNameError = !accountNameError;
    
    return hasGroup && hasAccountName && hasCurrency && hasNoNameError;
  }, [formData.group, formData.accountName, formData.currency, accountNameError]);

  return {
    getSelectedGroupDetails,
    handleDetailValueChange,
    hasFormChanged,
    isAccountFormValid,
  };
}
